import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BILLING_CLASS_LABEL, calcChangeQuote } from '@veta-desk/domain';
import { api, type ChangeDetail, type ChangeQuoteDraft } from '../lib/api';
import { dateTime } from '../lib/format';

// Veta Change · revisión del gestor: cotización por celda (riesgo repartido y redondeado),
// gestión como renglón, margen solo en el precio. Solo admin y gestor del servicio.

const field = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const area = 'w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 py-2 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';
const TONE = { contract: 'bg-accent-soft text-accent-dark', additional: 'bg-[#FFF4E5] text-[#6A3A00]', project: 'bg-[#FBEAEA] text-[#8F1D1D]' } as const;

const money = (n: number, c: string) => { try { return new Intl.NumberFormat('es-MX', { style: 'currency', currency: c }).format(n); } catch { return `${n} ${c}`; } };

export function ChangeQuotePanel({ d, onDone, onError }: { d: ChangeDetail; onDone: (d: ChangeDetail, msg: string) => void; onError: (e: unknown) => void }) {
  const number = d.ticket.number;
  const inReview = d.stage === 'manager_review';
  const [open, setOpen] = useState(inReview);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');
  useEffect(() => { setOpen(d.stage === 'manager_review'); }, [d.stage]);

  const draft = useQuery({ queryKey: ['change-quote', number, d.versions[0]?.version], queryFn: () => api<ChangeQuoteDraft>(`/change/${number}/quote`), enabled: open });
  const [f, setF] = useState({ riskPct: '10', managementPct: '12', marginPct: '0', rate: '', clientNotes: '' });
  useEffect(() => {
    if (!draft.data) return;
    const x = draft.data;
    setF({ riskPct: String(x.factors.riskPct), managementPct: String(x.factors.managementPct), marginPct: String(x.factors.marginPct),
      rate: x.rate !== null ? String(x.rate) : '', clientNotes: x.clientNotes });
  }, [draft.data]);

  const calc = useMemo(() => draft.data ? calcChangeQuote(draft.data.estimate, draft.data.phases,
    { riskPct: Number(f.riskPct) || 0, managementPct: Number(f.managementPct) || 0, marginPct: Number(f.marginPct) || 0 },
    f.rate ? Number(f.rate) : null, { roundingStep: draft.data.roundingStep, managementPctRange: draft.data.managementPctRange, sizing: draft.data.sizing }) : null, [draft.data, f]);

  const send = useMutation({
    mutationFn: () => api<ChangeDetail>(`/change/${number}/quote`, { method: 'POST', body: JSON.stringify({
      riskPct: Number(f.riskPct), managementPct: Number(f.managementPct), marginPct: Number(f.marginPct),
      rate: f.rate ? Number(f.rate) : null, clientNotes: f.clientNotes }) }),
    onSuccess: r => { setOpen(false); onDone(r, 'Cotización enviada al cliente.'); },
    onError
  });
  const giveBack = useMutation({
    mutationFn: () => api<ChangeDetail>(`/change/${number}/return`, { method: 'POST', body: JSON.stringify({ reason }) }),
    onSuccess: r => { setReturning(false); setReason(''); onDone(r, 'Regresada al consultor para reestimar.'); },
    onError
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(x => ({ ...x, [k]: e.target.value }));
  const q = d.quote;
  const x = draft.data;
  const est = (phase: string, disc: string) => x?.estimate.lines.find(l => l.phase === phase && l.discipline === disc)?.hours ?? 0;

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-[#E7D9C4] bg-[#FFFCF7] p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Revisión y cotización del gestor</h2>
        <span className="text-xs text-muted">Solo admin y gestor del servicio · el consultor no ve esta sección</span>
      </div>

      {d.adjustRequest && (
        <div className="rounded-md bg-[#FFF4E5] px-3 py-2.5 text-sm text-[#6A3A00]">
          <p><strong>El cliente pidió ajustes</strong> ({d.adjustRequest.by.replace(/\s*\(.*\)$/, '')} · {dateTime(d.adjustRequest.at)}){d.adjustRequest.reason ? `: “${d.adjustRequest.reason}”` : ''}</p>
          <p className="mt-1 text-xs">Elige la ruta: <strong>ajuste comercial</strong> (re-cotizas tú con la misma estimación) o <strong>reestimar</strong> (regresa al consultor).</p>
        </div>
      )}

      {q && !open && (
        <div className="text-sm">
          <p><span className="font-mono text-xl font-semibold">{q.totalHours} h</span>
            {q.price !== null && <span className="ml-2 font-semibold text-[#3D6B8A]">{money(q.price, q.currency)}</span>}
            <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-semibold ${q.billing === 'bag' ? TONE.contract : TONE.additional}`}>{q.billing === 'bag' ? 'Consume contrato' : 'Facturación adicional'}</span></p>
          <p className="mt-1 text-xs text-muted">v{q.version} · subtotal {q.subtotalHours} h (riesgo {q.riskPct} %) + gestión {q.managementHours} h ({q.managementPct} %) · margen {q.marginPct} % · enviada {dateTime(q.sentAt)} · vence {dateTime(q.validUntil)}{q.expired ? ' (vencida)' : ''}</p>
          {q.decision && <p className="mt-1 text-xs">Decisión del cliente: {{ approved: 'aprobada', rejected: 'rechazada', changes: 'pidió ajustes' }[q.decision.outcome]} · {q.decision.by}{q.decision.reason ? ` — “${q.decision.reason}”` : ''}</p>}
        </div>
      )}

      {d.permissions.quote && !open && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={btnPrimary} onClick={() => setOpen(true)}>{q ? 'Actualizar cotización (ajuste comercial)' : 'Cotizar'}</button>
          {inReview && <button type="button" className={btnSecondary} onClick={() => setReturning(true)}>Regresar al consultor</button>}
        </div>
      )}

      {open && (draft.isLoading || !x || !calc ? <p className="text-sm text-muted">Cargando…</p> : (
        <div className="flex flex-col gap-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-muted">Riesgo %<input type="number" min="0" className={field} value={f.riskPct} onChange={set('riskPct')} /></label>
            <label className="flex flex-col gap-1 text-xs text-muted">Gestión % <span className="text-[10px]">sugerido {x.managementPctRange[0]}–{x.managementPctRange[1]} %</span>
              <input type="number" min="0" className={`${field} ${calc.managementOutOfRange ? 'border-[#C2410C]' : ''}`} value={f.managementPct} onChange={set('managementPct')} /></label>
            <label className="flex flex-col gap-1 text-xs text-muted">Margen % <span className="text-[10px]">solo precio</span><input type="number" min="0" className={field} value={f.marginPct} onChange={set('marginPct')} /></label>
            {calc.billingClass === 'additional' && (
              <label className="flex flex-col gap-1 text-xs text-muted">Tarifa ({x.currency}/h) <span className="text-[10px]">{x.rateSource === 'overage' ? 'excedente / proyecto' : x.rateSource === 'sale' ? 'de venta (sin excedente)' : 'captúrala'}</span>
                <input type="number" min="0" step="0.01" className={field} value={f.rate} onChange={set('rate')} /></label>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted">
                  <th className="border-b border-line py-2 pr-3 font-semibold">Rubro</th>
                  <th className="border-b border-line px-2 py-2 text-right font-semibold">Funcional</th>
                  {x.estimate.requiresAbap && <th className="border-b border-line px-2 py-2 text-right font-semibold">ABAP</th>}
                  <th className="border-b border-line py-2 pl-2 text-right font-semibold">Cliente ve</th>
                </tr>
              </thead>
              <tbody>
                {x.phases.filter(p => calc.lines.some(l => l.phase === p.id)).map(p => {
                  const ql = (disc: string) => calc.lines.find(l => l.phase === p.id && l.discipline === disc)?.hours ?? 0;
                  return (
                    <tr key={p.id} className="border-b border-[#EFEDE9]">
                      <td className="py-1.5 pr-3">{p.name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums"><span className="text-muted">{est(p.id, 'functional') || '—'} →</span> {ql('functional') || '—'}</td>
                      {x.estimate.requiresAbap && <td className="px-2 py-1.5 text-right tabular-nums"><span className="text-muted">{est(p.id, 'abap') || '—'} →</span> {ql('abap') || '—'}</td>}
                      <td className="py-1.5 pl-2 text-right font-mono tabular-nums">{ql('functional') + ql('abap')}</td>
                    </tr>
                  );
                })}
                <tr className="border-b border-line font-semibold"><td className="py-1.5 pr-3" colSpan={x.estimate.requiresAbap ? 3 : 2}>Subtotal (consultor {x.estimate.consultantTotal} h + riesgo)</td><td className="py-1.5 pl-2 text-right font-mono">{calc.subtotalHours}</td></tr>
                <tr className="border-b border-line"><td className="py-1.5 pr-3" colSpan={x.estimate.requiresAbap ? 3 : 2}>Gestión del servicio ({f.managementPct || 0} %)</td><td className="py-1.5 pl-2 text-right font-mono">{calc.managementHours}</td></tr>
                <tr className="text-base font-semibold"><td className="py-2 pr-3" colSpan={x.estimate.requiresAbap ? 3 : 2}>Total</td><td className="py-2 pl-2 text-right font-mono">{calc.totalHours} h</td></tr>
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`rounded-md px-2 py-1 font-semibold ${TONE[calc.billingClass]}`}>{BILLING_CLASS_LABEL[calc.billingClass]}</span>
            {calc.billingClass !== x.estimate.preliminaryClass && (
              <span className="font-semibold text-[#9A3412]">
                Con este riesgo {calc.billingClass === 'additional' ? 'pasa a facturación adicional' : calc.billingClass === 'project' ? 'pasa de 180 h: es un proyecto (conviértelo)' : 'queda dentro del contrato'} (preliminar: {BILLING_CLASS_LABEL[x.estimate.preliminaryClass].toLowerCase()}).
              </span>
            )}
            {calc.managementOutOfRange && <span className="text-[#9A3412]">Gestión fuera del rango sugerido.</span>}
            {calc.price !== null && <span className="ml-auto text-sm font-semibold">{money(calc.price, x.currency)} <span className="text-xs font-normal text-muted">({money(calc.unitPrice!, x.currency)}/h)</span></span>}
          </div>

          <label className="flex flex-col gap-1 text-sm">Nota para el cliente (opcional)
            <textarea rows={2} className={area} value={f.clientNotes} onChange={set('clientNotes')} />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btnPrimary} disabled={send.isPending || calc.billingClass === 'project' || (calc.billingClass === 'additional' && !f.rate)} onClick={() => send.mutate()}>Enviar cotización al cliente</button>
            {inReview && <button type="button" className={btnSecondary} onClick={() => { setOpen(false); setReturning(true); }}>Regresar al consultor</button>}
            {!inReview && <button type="button" className={btnSecondary} onClick={() => setOpen(false)}>Cancelar</button>}
          </div>
        </div>
      ))}

      {returning && (
        <div className="flex flex-col gap-2">
          <label className="text-sm">¿Qué tiene que reestimar el consultor?
            <textarea rows={3} className={`${area} mt-1`} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
          </label>
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} disabled={giveBack.isPending || reason.trim().length < 5} onClick={() => giveBack.mutate()}>Regresar al consultor</button>
            <button type="button" className={btnSecondary} onClick={() => setReturning(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </section>
  );
}
